import { isWithinInterval, parseISO } from 'date-fns'

/** Minimal shape of an activity that can be removed from the salary calculation. */
export interface RejectedActivityInput {
  id: string
  activityType: string
  datetimeStart: string
  datetimeEnd: string
  excludeFromSalaryCalculation: boolean
  excludeBefore?: string | null
  excludeAfter?: string | null
  excludeComment?: string | null
}

export interface RejectedActivityWorkPeriod {
  activities: readonly RejectedActivityInput[]
  estimateActivities: readonly RejectedActivityInput[]
}

export interface RejectedActivityGroup {
  workPeriods: readonly RejectedActivityWorkPeriod[]
}

/** A continuous stretch of an activity that is not paid. */
export interface RejectedSpan {
  start: Date
  end: Date
}

export interface RejectedActivity {
  id: string
  activityType: string
  /** The activity's own bounds, regardless of how much of it was rejected. */
  start: Date
  end: Date
  /** The parts of the activity that were removed from the salary calculation. */
  spans: RejectedSpan[]
  /** Total rejected time, in seconds. */
  seconds: number
  /** True when only a part of the activity was clipped away. */
  isPartial: boolean
  /** True for rFMS estimate placeholders rather than real driver activities. */
  isEstimate: boolean
  /** The reason given when the activity was removed from the calculation. */
  comment: string
}

export interface Interval {
  start: Date
  end: Date
}

/**
 * Format a second count as "HH:MM" (hours may exceed two digits). Seconds are
 * truncated, matching how the rest of the overview renders durations.
 */
export function formatSecondsAsHourMinute(total: number): string {
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * The unpaid stretches of an excluded activity. `excludeBefore` / `excludeAfter`
 * mark where the *paid* span starts and ends, so the rejected time is what falls
 * outside them; with neither set (or both sitting on the activity's own bounds)
 * the whole activity is dropped from the calculation. Mirrors the clipping the
 * backend applies in `salary/util.py`.
 */
function getRejectedSpans(
  activity: RejectedActivityInput,
  start: Date,
  end: Date
): RejectedSpan[] {
  const before = activity.excludeBefore
    ? parseISO(activity.excludeBefore)
    : null
  const after = activity.excludeAfter ? parseISO(activity.excludeAfter) : null
  const clipsStart = !!before && before.getTime() !== start.getTime()
  const clipsEnd = !!after && after.getTime() !== end.getTime()

  if (!clipsStart && !clipsEnd) return [{ start, end }]

  const paidStart = clipsStart && before > start ? before : start
  const paidEnd = clipsEnd && after < end ? after : end

  const spans: RejectedSpan[] = []
  if (paidStart > start) spans.push({ start, end: paidStart })
  if (paidEnd < end) spans.push({ start: paidEnd, end })

  return spans
}

function toRejectedActivity(
  activity: RejectedActivityInput,
  isEstimate: boolean
): RejectedActivity | null {
  if (!activity.excludeFromSalaryCalculation) return null

  const start = parseISO(activity.datetimeStart)
  const end = parseISO(activity.datetimeEnd)
  if (end <= start) return null

  const spans = getRejectedSpans(activity, start, end)
  const seconds = spans.reduce(
    (acc, span) => acc + (span.end.getTime() - span.start.getTime()) / 1000,
    0
  )
  if (seconds <= 0) return null

  return {
    id: activity.id,
    activityType: activity.activityType,
    start,
    end,
    spans,
    seconds,
    isPartial: seconds < (end.getTime() - start.getTime()) / 1000,
    isEstimate,
    comment: activity.excludeComment?.trim() ?? '',
  }
}

/** Unique across a work period's real activities and its estimates. */
export function getRejectedActivityKey(rejected: RejectedActivity): string {
  return `${rejected.isEstimate ? 'estimate' : 'activity'}-${rejected.id}`
}

function byStart(left: RejectedActivity, right: RejectedActivity): number {
  return left.start.getTime() - right.start.getTime()
}

/** Every activity of one work period removed from the salary calculation. */
export function getWorkPeriodRejectedActivities(
  workPeriod: RejectedActivityWorkPeriod
): RejectedActivity[] {
  return [
    ...workPeriod.activities.map(activity =>
      toRejectedActivity(activity, false)
    ),
    ...workPeriod.estimateActivities.map(activity =>
      toRejectedActivity(activity, true)
    ),
  ]
    .filter((rejected): rejected is RejectedActivity => !!rejected)
    .sort(byStart)
}

/**
 * Every activity removed from the salary calculation inside the report window,
 * ordered by when it started. Activities are placed by their start, so a shift
 * crossing the month boundary reports each rejection in exactly one month.
 */
export function getRejectedActivities(
  groups: readonly RejectedActivityGroup[],
  window: Interval
): RejectedActivity[] {
  const byKey = new Map<string, RejectedActivity>()

  groups.forEach(group =>
    group.workPeriods.forEach(workPeriod =>
      getWorkPeriodRejectedActivities(workPeriod).forEach(rejected => {
        if (!isWithinInterval(rejected.start, window)) return
        byKey.set(getRejectedActivityKey(rejected), rejected)
      })
    )
  )

  return [...byKey.values()].sort(byStart)
}

/** Total rejected time across the period, in seconds. */
export function getRejectedActivitiesTotal(
  rejected: readonly RejectedActivity[]
): number {
  return rejected.reduce((acc, activity) => acc + activity.seconds, 0)
}
